/**
 * Rich-text sanitizer and the link rules shared with `link` fields.
 *
 * Customer-entered HTML ends up in a public website, so this is a security boundary
 * (decision D7). It is an ALLOW-list rebuilt from a token stream: the input is never
 * patched or regex-stripped, the output is re-emitted from scratch using only the tags the
 * field permits, every attribute except a vetted `href` is dropped, and all text is
 * re-escaped. Anything the tokenizer does not hand over as an allowed tag or as text
 * simply has no way into the output.
 *
 * `htmlparser2` rather than DOMPurify because this runs on the server, in the browser and
 * inside site repos' build checks; DOMPurify needs a DOM. htmlparser2 is a pure tokenizer
 * — no DOM, no resource loading.
 */
import { Parser } from 'htmlparser2';

import type { WebsiteFieldSchemaModel } from './types';

/** Elements whose entire content is dropped, not just the tags. */
const DROPPED_WITH_CONTENT: ReadonlySet<string> = new Set([
	'script',
	'style',
	'iframe',
	'object',
	'embed',
	'svg',
	'math',
	'template',
	'noscript',
	'head',
	'title',
	'textarea',
	'select',
	'form',
	'canvas',
	'audio',
	'video',
]);

/** Source tag → the tag written out. Pasted `<b>`/`<i>` become their semantic forms. */
const TAG_ALIASES: Readonly<Record<string, string>> = { b: 'strong', i: 'em' };

const VOID_TAGS: ReadonlySet<string> = new Set(['br']);

/** Runs on sanitized output only, where a paragraph tag never carries an attribute. */
const TRAILING_EMPTY_PARAGRAPHS = /(?:<p>(?:<br>)?<\/p>)+$/;

function allowedTagsFor(field: WebsiteFieldSchemaModel): ReadonlySet<string> {
	const tags = new Set<string>();

	if (field.marks.includes('bold')) {
		tags.add('strong');
	}
	if (field.marks.includes('italic')) {
		tags.add('em');
	}
	if (field.marks.includes('link')) {
		tags.add('a');
	}
	if (field.blocks.includes('paragraph')) {
		tags.add('p');
		tags.add('br');
	}
	if (field.blocks.includes('list')) {
		tags.add('ul');
		tags.add('ol');
		tags.add('li');
	}

	return tags;
}

function escapeText(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function escapeAttribute(text: string): string {
	return escapeText(text).replace(/"/g, '&quot;');
}

function hasControlOrSpace(value: string): boolean {
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i);
		if (code <= 0x20 || code === 0x7f) {
			return true;
		}
	}
	return false;
}

export interface WebsiteHrefRulesModel {
	/** Rich text may link to plain http sites; `link` fields may not. */
	isHttpAllowed: boolean;
}

/**
 * Is this a destination a customer may link to? Internal paths, `https:`, `mailto:` and
 * `tel:` — and `http:` where the caller allows it. Everything else is refused, including
 * `javascript:`, `data:`, `vbscript:`, protocol-relative `//host` (an open redirect in
 * disguise), backslash tricks, and anything with whitespace or control characters in it
 * (browsers strip those, which is how `java\tscript:` works).
 */
export function isAllowedHref(
	href: string,
	rules: WebsiteHrefRulesModel,
): boolean {
	if (href.length === 0 || href.length > 2048 || hasControlOrSpace(href)) {
		return false;
	}

	if (href.includes('\\')) {
		return false;
	}

	if (href.startsWith('/')) {
		return !href.startsWith('//');
	}

	if (href.startsWith('#')) {
		return href.length > 1;
	}

	const lower = href.toLowerCase();

	if (lower.startsWith('https://')) {
		return lower.length > 'https://'.length;
	}

	if (lower.startsWith('http://')) {
		return rules.isHttpAllowed && lower.length > 'http://'.length;
	}

	if (lower.startsWith('mailto:')) {
		return /^mailto:[^@\s]+@[^@\s]+$/.test(lower);
	}

	if (lower.startsWith('tel:')) {
		return /^tel:\+?[0-9().-]+$/.test(lower);
	}

	return false;
}

interface ListItemFrameModel {
	/** Index in the output where the item's own content begins. */
	contentStart: number;
	/** Output positions of the opening and closing tag of each paragraph DIRECTLY in the item. */
	paragraphs: Array<{ open: number; close: number }>;
}

/**
 * A list item has ONE stored form: `<li>text</li>`, never `<li><p>text</p></li>`. The form's
 * text editor always writes the second while a site's own content is written the first, and
 * keeping both meant that merely opening a page reported unsaved work, and that a customer's
 * first edit rewrote the markup of every list item in the field (paragraph margins inside a
 * list render differently, so that is a visible change nobody asked for).
 *
 * Only the unambiguous case is touched: exactly one paragraph, sitting first in the item. An
 * item that really has several paragraphs keeps them.
 */
function unwrapLoneParagraph(
	output: Array<string>,
	frame: ListItemFrameModel | undefined,
): void {
	const paragraph = frame?.paragraphs.at(0);

	if (
		frame == null ||
		paragraph == null ||
		frame.paragraphs.length !== 1 ||
		paragraph.open !== frame.contentStart ||
		paragraph.close === -1
	) {
		return;
	}

	output[paragraph.open] = '';
	output[paragraph.close] = '';
}

/**
 * Reduce HTML to what the field allows. Idempotent: sanitizing already-sanitized output
 * returns it unchanged, which is what lets the validator check "is this value sanitized?"
 * by comparing a value with its own sanitized form.
 */
export function sanitizeRichtext(
	html: string,
	field: WebsiteFieldSchemaModel,
): string {
	const allowed = allowedTagsFor(field);
	const output: Array<string> = [];
	const openTags: Array<string> = [];
	// One frame per open list item, so that an item holding exactly ONE paragraph, as its
	// first child, can be written without it (see `unwrapLoneParagraph`).
	const listItems: Array<ListItemFrameModel> = [];
	let droppedDepth = 0;

	const parser = new Parser(
		{
			onopentag(rawName, attributes): void {
				const name = rawName.toLowerCase();

				if (droppedDepth > 0 || DROPPED_WITH_CONTENT.has(name)) {
					if (!VOID_TAGS.has(name)) {
						droppedDepth++;
					}
					return;
				}

				const tag = TAG_ALIASES[name] ?? name;

				if (!allowed.has(tag)) {
					return;
				}

				if (VOID_TAGS.has(tag)) {
					output.push(`<${tag}>`);
					return;
				}

				if (tag === 'a') {
					const href = attributes['href'];

					// A link with no acceptable destination keeps its text but loses the tag.
					if (href == null || !isAllowedHref(href, { isHttpAllowed: true })) {
						openTags.push('');
						return;
					}

					const isExternal = /^https?:/i.test(href);
					output.push(
						`<a href="${escapeAttribute(href)}"${isExternal ? ' rel="noopener noreferrer"' : ''}>`,
					);
					openTags.push('a');
					return;
				}

				if (tag === 'p' && openTags.at(-1) === 'li') {
					listItems.at(-1)?.paragraphs.push({ open: output.length, close: -1 });
				}

				output.push(`<${tag}>`);
				openTags.push(tag);

				if (tag === 'li') {
					listItems.push({ contentStart: output.length, paragraphs: [] });
				}
			},
			ontext(text): void {
				if (droppedDepth === 0) {
					output.push(escapeText(text));
				}
			},
			onclosetag(rawName): void {
				const name = rawName.toLowerCase();

				if (droppedDepth > 0) {
					if (!VOID_TAGS.has(name)) {
						droppedDepth--;
					}
					return;
				}

				const tag = TAG_ALIASES[name] ?? name;

				if (!allowed.has(tag) || VOID_TAGS.has(tag)) {
					return;
				}

				const opened = openTags.pop();

				if (opened === 'li') {
					unwrapLoneParagraph(output, listItems.pop());
				}

				if (opened != null && opened.length > 0) {
					output.push(`</${opened}>`);
				}

				if (opened === 'p' && openTags.at(-1) === 'li') {
					const paragraph = listItems.at(-1)?.paragraphs.at(-1);

					if (paragraph != null && paragraph.close === -1) {
						paragraph.close = output.length - 1;
					}
				}
			},
		},
		{ decodeEntities: true, lowerCaseTags: true, recognizeSelfClosing: true },
	);

	parser.write(html);
	parser.end();

	// The text editor keeps an empty paragraph after a closing list so there is somewhere to
	// type. Stored, it is a blank gap on the site and a difference from what was loaded, so
	// trailing ones are not kept. An empty paragraph FOLLOWED by content is someone's spacing
	// and stays.
	return output.join('').replace(TRAILING_EMPTY_PARAGRAPHS, '');
}

/** Visible character count of rich text, for `maxLength`. */
export function richtextTextLength(html: string): number {
	let length = 0;
	const parser = new Parser(
		{
			ontext(text): void {
				length += text.length;
			},
		},
		{ decodeEntities: true },
	);

	parser.write(html);
	parser.end();

	return length;
}
